import { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import './App.css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PrivacyPolicy from './PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions';
import ThankYou from './ThankYou';
import ScrollToTop from './ScrollToTop';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    id: 1,
    quote: "Great communication, great care, secure facility. The food is fantastic. I'm so glad to have my brother close and in this facility.",
    name: 'Claudia Jones',
    role: 'Sister of resident',
    initial: 'C',
  },
  {
    id: 2,
    quote: "The staff at Gardens of North Port truly goes above and beyond. My mother is happier and healthier than she has been in years. We couldn't be more grateful.",
    name: 'Robert Martinez',
    role: 'Son of resident',
    initial: 'R',
  },
  {
    id: 3,
    quote: "From the moment we toured, we knew this was the right place. The warmth of the team and the quality of care is unmatched. Highly recommend to any family.",
    name: 'Patricia Williams',
    role: 'Daughter of resident',
    initial: 'P',
  },
];

// Gallery images array
const galleryImages = [
  { id: 1, src: 'gnpgallery-images-01.webp', alt: 'Elegant lobby seating area' },
  { id: 2, src: 'gnp-gallery-images-02.webp', alt: 'Outdoor entrance sign' },
  { id: 3, src: 'gnp-gallery-images-03.webp', alt: 'Private dining room' },
  { id: 4, src: 'gnp-gallery-images-04-1.webp', alt: 'Comfortable bedroom suite' },
  { id: 5, src: 'gnp-gallery-images-09.webp', alt: 'Beautiful fountain exterior' },
  { id: 6, src: 'gnp-gallery-images-05.webp', alt: 'Aerial community view' },
  { id: 7, src: 'gnp-gallery-images-07.webp', alt: 'Building front entrance' },
  { id: 8, src: 'gnp-gallery-images-06-1.webp', alt: 'Elegant lobby entrance' },
  { id: 9, src: 'gnp-gallery-images-08.webp', alt: 'Fine dining room' },
];

// Residence images array
const residenceImages = [
  { id: 1, src: 'evergreen-garden-apartment01.webp', alt: 'Evergreen Garden Apartment', title: 'Evergreen Garden Apartment' },
  { id: 2, src: 'pine-cone-studio_1x.webp', alt: 'Pine Cone Studio', title: 'Pine Cone Studio' },
  { id: 3, src: 'evergreen-II-garden-apartment_1x.webp', alt: 'Evergreen II - Garden Apartment', title: 'Evergreen II - Garden Apartment' },
  { id: 4, src: 'spruce-studio-apartment_1x.webp', alt: 'Spruce - Studio Apartment', title: 'Spruce - Studio Apartment' },
];

// Amenities images array
const amenitiesImages = [
  { id: 1, src: 'chef-prepared01.webp', alt: 'Chef-prepared meals', title: 'Chef-prepared meals - Three meals daily, fresh ingredients, bistro snacks' },
  { id: 2, src: 'wellness-center01.webp', alt: 'Wellness centre', title: 'Wellness centre - Fitness, mobility, podiatrist visits' },
  { id: 3, src: 'salon-spa01.webp', alt: 'Salon & spa', title: 'Salon & spa - Professional grooming, hairstyling on site' },
  { id: 4, src: 'safety-security01.webp', alt: '24/7 safety & security', title: '24/7 safety & security - Secure environment, attentive overnight staff' },
];

function HomePage() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [residenceOpen, setResidenceOpen] = useState(false);
  const [residenceIndex, setResidenceIndex] = useState(0);
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [amenitiesIndex, setAmenitiesIndex] = useState(0);

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', bestTime: '', careType: '', message: ''
  });
  const [formStatus, setFormStatus] = useState('idle'); // idle | submitting | error
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');
    try {
      const res = await fetch('/gnp-backend/forms.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        navigate('/thank-you');
      } else {
        setFormStatus('error');
        setFormError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Unable to send your request. Please call us at (941) 249-2344.');
    }
  };

  const prev = () => setActiveIndex(i => (i === 0 ? testimonials.length - 1 : i - 1));
  const next = () => setActiveIndex(i => (i === testimonials.length - 1 ? 0 : i + 1));
  const current = testimonials[activeIndex];
  return (
    <div className="App">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-logo">
          <Link to="/">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/gnp-logo01.webp`} 
              alt="Gardens of North Port" 
              className="logo-image"
            />
          </Link>
        </div>

        <ul className="navbar-links">
          <li><Link to="/#home">Home</Link></li>
          <li><Link to="/#care-options">Care Options</Link></li>
          <li><Link to="/#amenities">Amenities</Link></li>
          <li><Link to="/#residences">Residences</Link></li>
          <li><Link to="/#testimonials">Testimonials</Link></li>
        </ul>

        <div className="navbar-cta">
          <span className="navbar-phone-text">(941) 249-2344</span>
          <a href="tel:9412492344" className="navbar-phone-btn" aria-label="Call us">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.6 10.8C7.8 13.2 9.8 15.2 12.2 16.4L14.1 14.5C14.35 14.25 14.7 14.18 15 14.32C16.05 14.7 17.2 14.9 18.4 14.9C18.9 14.9 19.3 15.3 19.3 15.8V18.4C19.3 18.9 18.9 19.3 18.4 19.3C10.3 19.3 3.7 12.7 3.7 4.6C3.7 4.1 4.1 3.7 4.6 3.7H7.2C7.7 3.7 8.1 4.1 8.1 4.6C8.1 5.8 8.3 6.95 8.68 8C8.82 8.3 8.75 8.65 8.5 8.9L6.6 10.8Z" fill="currentColor" />
            </svg>
          </a>
        </div>
      </nav>

      {/* Hero Banner */}
      <section id="home" className="hero-banner" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/home-banner01.webp)` }}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-star">★</span>
            Now Accepting New Residents &middot; North Port, FL
          </div>

          <h1 className="hero-heading">
            Is your loved one ready for<br />
            a safer, more joyful life?
          </h1>

          <p className="hero-subtext">
            Gardens of North Port offers warm, professional care for seniors who deserve more than just a place to live &mdash; 75 thoughtfully designed residences, chef-prepared meals, and a team that truly cares.
          </p>

          <div className="hero-rating">
            <span className="stars">★★★★★</span>
            <span className="rating-score">4.9/5</span>
            <span className="rating-platform">Google &amp; Facebook</span>
          </div>

          <div className="hero-buttons">
            <a href="#tour" className="btn-primary">Schedule a Free Tour &rarr;</a>
            <a href="tel:9412492344" className="btn-secondary">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.6 10.8C7.8 13.2 9.8 15.2 12.2 16.4L14.1 14.5C14.35 14.25 14.7 14.18 15 14.32C16.05 14.7 17.2 14.9 18.4 14.9C18.9 14.9 19.3 15.3 19.3 15.8V18.4C19.3 18.9 18.9 19.3 18.4 19.3C10.3 19.3 3.7 12.7 3.7 4.6C3.7 4.1 4.1 3.7 4.6 3.7H7.2C7.7 3.7 8.1 4.1 8.1 4.6C8.1 5.8 8.3 6.95 8.68 8C8.82 8.3 8.75 8.65 8.5 8.9L6.6 10.8Z" fill="currentColor" />
              </svg>
              (941) 249-2344
            </a>
          </div>

          <div className="hero-trust">
            <svg className="shield-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V6L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            FL Lic. #10843 &middot; State licensed &amp; inspected
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator">
          <div className="mouse">
            <div className="mouse-wheel" />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="testimonials-header">
          <span className="testimonials-label">WHAT FAMILIES SAY</span>
          <h2 className="testimonials-heading">
            Real stories from <em>residents' families</em>
          </h2>
          <p className="testimonials-subtext">
            Over 40 five-star reviews from families who trusted us with the people they love most.
          </p>
        </div>

        <div className="testimonials-card-wrap">
          <div className="testimonials-card">
            <div className="card-top">
              <span className="card-stars">★★★★★</span>
              <span className="card-verified">Verified Review</span>
            </div>
            <div className="card-quote-mark">&ldquo;&rdquo;</div>
            <p className="card-text">{current.quote}</p>
            <div className="card-author">
              <div className="card-avatar">{current.initial}</div>
              <div className="card-author-info">
                <span className="card-author-name">{current.name}</span>
                <span className="card-author-role">{current.role}</span>
              </div>
            </div>
          </div>

          <div className="testimonials-controls">
            <button className="ctrl-btn ctrl-prev" onClick={prev} aria-label="Previous">
              &#8249;
            </button>
            <button className="ctrl-btn ctrl-next" onClick={next} aria-label="Next">
              &#8250;
            </button>
          </div>

          <div className="testimonials-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`dot${i === activeIndex ? ' dot-active' : ''}`}
                onClick={() => setActiveIndex(i)}
                aria-label={`Go to review ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Care Options Section */}
      <section id="care-options" className="care-section">
        <div className="care-header">
          <span className="care-label">CARE OPTIONS</span>
          <h2 className="care-heading">
            Freedom when you want it,<br />
            <em>support when you need it</em>
          </h2>
          <p className="care-subtext">
            Both pathways share the same beautiful community — residents can<br />
            transition between levels without uprooting their lives.
          </p>
        </div>

        <div className="care-availability">
          <div className="care-avail-left">
            <div className="avail-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12L12 4l9 8v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M9 21V12h6v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <span className="avail-title">Limited availability</span>
              <span className="avail-sub">Only 75 residences in our community</span>
            </div>
          </div>
          <a href="#residences" className="avail-btn">Check Current Openings →</a>
        </div>

        <div className="care-cards">
          {/* Independent Living */}
          <div className="care-card">
            <div className="care-card-img-wrap">
              <img src={`${process.env.PUBLIC_URL}/assets/independent-living01.webp`} alt="Independent Living" className="care-card-img" />
              <div className="care-card-icon care-card-icon--green">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12L12 4l9 8v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                  <path d="M9 21V12h6v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="care-card-body">
              <span className="care-card-tag care-card-tag--green">Active, social, worry-free</span>
              <h3 className="care-card-title">Independent Living</h3>
              <p className="care-card-desc">Enjoy full independence in a vibrant community with meals, activities and housekeeping.</p>
              <ul className="care-card-list">
                <li>Private garden apartment</li>
                <li>Chef-prepared meals daily</li>
                <li>Social events &amp; outings</li>
                <li>Housekeeping included</li>
              </ul>
              <a href="#tour" className="care-card-link">See If It Fits You →</a>
            </div>
          </div>

          {/* Assisted Living */}
          <div className="care-card">
            <div className="care-card-img-wrap">
              <img src={`${process.env.PUBLIC_URL}/assets/assisted-living01.webp`} alt="Assisted Living" className="care-card-img" />
              <div className="care-card-icon care-card-icon--terracotta">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 21C12 21 4 14.5 4 9a4 4 0 018-1.5A4 4 0 0120 9c0 5.5-8 12-8 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="care-card-body">
              <span className="care-card-tag care-card-tag--terracotta">Expert care, dignity intact</span>
              <h3 className="care-card-title">Assisted Living</h3>
              <p className="care-card-desc">Personal assistance with daily tasks alongside 24/7 professional staff — always.</p>
              <ul className="care-card-list">
                <li>Personalised care plan</li>
                <li>Medication management</li>
                <li>In-home assistance</li>
                <li>Podiatrist on site</li>
              </ul>
              <a href="#tour" className="care-card-link">See If It Fits You →</a>
            </div>
          </div>

          {/* Memory Care */}
          <div className="care-card">
            <div className="care-card-img-wrap">
              <img src={`${process.env.PUBLIC_URL}/assets/in-home-medical-care01.webp`} alt="Memory Care" className="care-card-img" />
              <div className="care-card-icon care-card-icon--purple">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5.64 5.64l1.42 1.42M16.95 16.95l1.41 1.41M5.64 18.36l1.42-1.42M16.95 7.05l1.41-1.41" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
            <div className="care-card-body">
              <span className="care-card-tag care-card-tag--purple">Specialized support</span>
              <h3 className="care-card-title">In-Home Medical Care</h3>
              <p className="care-card-desc">Compassionate care designed for residents with different medical needs.</p>
              <ul className="care-card-list">
                <li>Specialized staff training</li>
                <li>Congestive heart failure management</li>
                <li>Diabetes care and management</li>
                <li>Vascular wound care</li>
              </ul>
              <a href="#tour" className="care-card-link">See If It Fits You →</a>
            </div>
          </div>
        </div>

        <p className="care-footer-note">
          <strong>Both options include:</strong> 24/7 security, chef meals, wellness centre access &amp; salon services
        </p>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="amenities-section">
        {/* Left Column */}
        <div className="amenities-left">
          <span className="amenities-label">LIFE HERE</span>
          <h2 className="amenities-heading">
            Everything your loved one needs,<br />
            <em>under one roof</em>
          </h2>
          <p className="amenities-subtext">
            Designed to feel like home, not a facility. Our amenities are thoughtfully curated to enhance quality of life and promote independence.
          </p>
          <a href="#tour" className="amenities-btn">Schedule a Tour →</a>
          <div className="amenities-stats">
            <div className="amenities-stat">
              <span className="stat-number stat-number--dark">24/7</span>
              <span className="stat-label">Professional Care</span>
            </div>
            <div className="amenities-stat">
              <span className="stat-number stat-number--terracotta">50+</span>
              <span className="stat-label">Activities Monthly</span>
            </div>
          </div>
        </div>

        {/* Right Column — image mosaic */}
        <div className="amenities-right">
          {/* Top — full width */}
          <div className="amen-img-cell amen-img-cell--full">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/chef-prepared01.webp`} 
              alt="Chef-prepared meals" 
              className="amen-img" 
              onClick={() => {
                setAmenitiesIndex(0);
                setAmenitiesOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <span className="amen-pill">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 11V3h5v5M3 11h18M3 11v9h18v-9M8 21v-5a2 2 0 014 0v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Chef-prepared meals
            </span>
            <span className="amen-caption">Three meals daily, fresh ingredients, bistro snacks</span>
          </div>

          {/* Middle row — two equal */}
          <div className="amen-img-cell">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/wellness-center01.webp`} 
              alt="Wellness centre" 
              className="amen-img" 
              onClick={() => {
                setAmenitiesIndex(1);
                setAmenitiesOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <span className="amen-pill">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><line x1="4" y1="22" x2="4" y2="15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              Wellness centre
            </span>
            <span className="amen-caption">Fitness, mobility, podiatrist visits</span>
          </div>
          <div className="amen-img-cell">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/salon-spa01.webp`} 
              alt="Salon & spa" 
              className="amen-img" 
              onClick={() => {
                setAmenitiesIndex(2);
                setAmenitiesOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <span className="amen-pill">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" strokeWidth="1.6"/><path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><line x1="9" y1="9" x2="9.01" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="15" y1="9" x2="15.01" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              Salon &amp; spa
            </span>
            <span className="amen-caption">Professional grooming, hairstyling on site</span>
          </div>

          {/* Bottom — narrower centered */}
          <div className="amen-img-cell amen-img-cell--bottom">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/safety-security01.webp`} 
              alt="24/7 safety & security" 
              className="amen-img" 
              onClick={() => {
                setAmenitiesIndex(3);
                setAmenitiesOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <span className="amen-pill">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/><path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
              24/7 safety &amp; security
            </span>
            <span className="amen-caption">Secure environment, attentive overnight staff</span>
          </div>
        </div>
      </section>

      {/* Residences Section */}
      <section id="residences" className="residences-section">
        <div className="residences-header">
          <span className="residences-label">RESIDENCES</span>
          <h2 className="residences-heading">
            Choose the home that <em>fits your lifestyle</em>
          </h2>
          <p className="residences-subtext">
            Studio and garden apartments — all move-in ready with natural light and garden views.
          </p>
        </div>

        <div className="res-grid">
          {/* Row 1 — Left */}
          <div className="res-card res-card--r1left">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/evergreen-garden-apartment01.webp`} 
              alt="Evergreen Garden Apartment" 
              className="res-img" 
              onClick={() => {
                setResidenceIndex(0);
                setResidenceOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <div className="res-overlay" />
            <span className="res-pill">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>
              Garden View
            </span>
            <div className="res-body">              
              <h3 className="res-title">Evergreen Garden Apartment</h3>     
              <a href="#tour" className="res-link">Click For Virtual Tour →</a>
            </div>
          </div>

          {/* Row 1 — Right */}
          <div className="res-card res-card--r1right">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/pine-cone-studio_1x.webp`} 
              alt="Pine Cone Studio" 
              className="res-img" 
              onClick={() => {
                setResidenceIndex(1);
                setResidenceOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <div className="res-overlay" />
            <div className="res-body">
              <h3 className="res-title">Pine Cone Studio</h3>
               <a href="#tour" className="res-link">Click For Virtual Tour →</a>
            </div>
          </div>

          {/* Row 2 — Left */}
          <div className="res-card res-card--r2left">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/evergreen-II-garden-apartment_1x.webp`} 
              alt="Evergreen II - Garden Apartment" 
              className="res-img" 
              onClick={() => {
                setResidenceIndex(2);
                setResidenceOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <div className="res-overlay" />
            <div className="res-body">
              <h3 className="res-title">Evergreen II - Garden Apartment</h3>
               <a href="#tour" className="res-link">Click For Virtual Tour →</a>
            </div>
          </div>

          {/* Row 2 — Right */}
          <div className="res-card res-card--r2right">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/spruce-studio-apartment_1x.webp`} 
              alt="Spruce - Studio Apartment" 
              className="res-img" 
              onClick={() => {
                setResidenceIndex(3);
                setResidenceOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            />
            <div className="res-overlay" />
            <div className="res-body">
              <h3 className="res-title">Spruce - Studio Apartment</h3>
              <a href="#tour" className="res-link">Click For Virtual Tour →</a>
            </div>
          </div>
        </div>

        <div className="res-features">
          <div className="res-feature">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
            Large walk-in closets
          </div>
          <div className="res-feature">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2a5 5 0 100 10A5 5 0 0012 2z" stroke="currentColor" strokeWidth="1.5"/><path d="M12 12v10M8 22h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Natural garden light
          </div>
          <div className="res-feature">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Fully furnished
          </div>
          <div className="res-feature">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0M12 20h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            High-speed internet
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="gallery-header">
          <h2 className="gallery-heading">
            Gardens of Venice Retirement Residence Photos
          </h2>
        </div>

        <div className="gallery-grid">
          {galleryImages.map((image, index) => (
            <div 
              key={image.id} 
              className={`gallery-item gallery-item-${image.id}`}
              onClick={() => {
                setGalleryIndex(index);
                setGalleryOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/assets/${image.src}`} 
                alt={image.alt} 
                className="gallery-img" 
              />
            </div>
          ))}
        </div>
      </section>

      {/* Steps Section */}
      <section className="steps-section">
        <div className="steps-header">
          <span className="steps-label">WHAT HAPPENS NEXT</span>
          <h2 className="steps-heading">Three simple steps to finding the right home</h2>
          <p className="steps-subtext">Just a friendly conversation and a live tour of how we treat our residents.</p>
        </div>

        <div className="steps-row">
          {/* Step 1 */}
          <div className="step">
            <div className="step-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <h3 className="step-title">Schedule A Tour</h3>
            <p className="step-desc">Visit our community, enjoy a complimentary meal, and meet our caring team. Enjoy a day with our beloved residents.</p>
          </div>

          <div className="step-connector"><span className="step-dot" /></div>

          {/* Step 2 */}
          <div className="step">
            <div className="step-icon step-icon--center">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M9 21V12h6v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="step-title">Find Your Perfect Fit</h3>
            <p className="step-desc">We’ll help you choose the right residence and care level based on your needs, preferences, and budget.</p>
          </div>

          <div className="step-connector"><span className="step-dot" /></div>

          {/* Step 3 */}
          <div className="step">
            <div className="step-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21C12 21 4 14.5 4 9a4 4 0 018-1.5A4 4 0 0120 9c0 5.5-8 12-8 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="step-title">Move in &amp; Relax</h3>
            <p className="step-desc">Our team makes the transition smooth and stress-free. Welcome to your new home!</p>
          </div>
        </div>

        <div className="steps-badges">
          <span className="steps-badge"><span className="steps-check">✓</span> Calm Environment</span>
          <span className="steps-badge"><span className="steps-check">✓</span> Smooth Process</span>
          <span className="steps-badge"><span className="steps-check">✓</span> Rest Assured</span>
        </div>
      </section>

      {/* Tour CTA Section */}
      <section id="tour" className="tour-section">
        {/* Left */}
        <div className="tour-left">
          <span className="tour-label">FREE &middot; NO OBLIGATION</span>
          <h2 className="tour-heading">
            Schedule your <em>free lunch tour</em> today
          </h2>
          <p className="tour-subtext">
            See why families choose Gardens of North Port — enjoy a meal, meet the team, and tour the community.
          </p>

          <div className="tour-contacts">
            <div className="tour-contact-item">
              <div className="tour-contact-icon tour-contact-icon--terracotta">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.6 10.8C7.8 13.2 9.8 15.2 12.2 16.4L14.1 14.5c.25-.25.6-.32.9-.18 1.05.38 2.2.58 3.4.58.5 0 .9.4.9.9v2.6c0 .5-.4.9-.9.9C10.3 19.3 3.7 12.7 3.7 4.6c0-.5.4-.9.9-.9H7.2c.5 0 .9.4.9.9 0 1.2.2 2.35.58 3.4.14.3.07.65-.18.9L6.6 10.8z" fill="currentColor"/>
                </svg>
              </div>
              <div>
                <span className="tour-contact-title">Prefer to call?</span>
                <a href="tel:9412492344" className="tour-phone">(941) 249-2344</a>
                <span className="tour-contact-sub">Available Monday–Saturday, 9am–8pm</span>
              </div>
            </div>

            <div className="tour-contact-item">
              <div className="tour-contact-icon tour-contact-icon--green">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <span className="tour-contact-title">Quick Response</span>
                <span className="tour-contact-sub">We reply within 1 hour during business hours</span>
              </div>
            </div>

            <div className="tour-contact-item">
              <div className="tour-contact-icon tour-contact-icon--purple">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                  <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.8"/>
                </svg>
              </div>
              <div>
                <span className="tour-contact-title">Visit Us</span>
                <span className="tour-contact-sub">4900 S Sumter Blvd, North Port, FL 34287</span>
              </div>
            </div>
          </div>

          <div className="tour-license">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V6L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <span className="tour-license-title">State Licensed &amp; Inspected</span>
              <span className="tour-license-sub">FL License #10843</span>
            </div>
          </div>
        </div>

        {/* Right — Form */}
        <div className="tour-form-card">
          <h3 className="tour-form-title">Reserve your tour spot</h3>

            <form onSubmit={handleFormSubmit} noValidate>
              <div className="tour-field">
                <label className="tour-field-label">Your name</label>
                <input
                  type="text"
                  name="name"
                  className="tour-input"
                  placeholder="Full name"
                  value={formData.name}
                  onChange={handleFieldChange}
                  required
                />
              </div>

              <div className="tour-field-row">
                <div className="tour-field">
                  <label className="tour-field-label">Phone number</label>
                  <input
                    type="tel"
                    name="phone"
                    className="tour-input"
                    placeholder="(941) 000-0000"
                    value={formData.phone}
                    onChange={handleFieldChange}
                    required
                  />
                </div>
                <div className="tour-field">
                  <label className="tour-field-label">Email (optional)</label>
                  <input
                    type="email"
                    name="email"
                    className="tour-input"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={handleFieldChange}
                  />
                </div>
              </div>

              <div className="tour-field-row">
                <div className="tour-field">
                  <label className="tour-field-label">Best time to call</label>
                  <select
                    name="bestTime"
                    className="tour-select"
                    value={formData.bestTime}
                    onChange={handleFieldChange}
                  >
                    <option value="">Select a time</option>
                    <option>Morning (9am–12pm)</option>
                    <option>Afternoon (12pm–5pm)</option>
                    <option>Evening (5pm–8pm)</option>
                  </select>
                </div>
                <div className="tour-field">
                  <label className="tour-field-label">Type of care</label>
                  <select
                    name="careType"
                    className="tour-select"
                    value={formData.careType}
                    onChange={handleFieldChange}
                  >
                    <option value="">Not sure yet</option>
                    <option>Independent Living</option>
                    <option>Assisted Living</option>
                    <option>Memory Care</option>
                  </select>
                </div>
              </div>

              <div className="tour-field">
                <label className="tour-field-label">Message (optional)</label>
                <textarea
                  name="message"
                  className="tour-textarea"
                  placeholder="Tell us about your needs or questions..."
                  maxLength={500}
                  value={formData.message}
                  onChange={handleFieldChange}
                />
                <span className="tour-char-count">{formData.message.length}/500 characters</span>
              </div>

              {formStatus === 'error' && (
                <p className="tour-error-msg">{formError}</p>
              )}

              <button className="tour-submit-btn" type="submit" disabled={formStatus === 'submitting'}>
                {formStatus === 'submitting' ? (
                  'Sending…'
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Request a Lunch Tour
                  </>
                )}
              </button>
              <p className="tour-privacy">No cost, no obligation. We respect your privacy.</p>
            </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="custom-footer">
        <div className="custom-footer-main">
          {/* About */}
          <div>
            <h2>About Gardens of North Port</h2>
            <p>
              Gardens of North Port is a caring senior living community where comfort, dignity, and connection come together in a beautiful North Port setting. With thoughtfully prepared meals, welcoming residences, enriching activities, and attentive support, we help residents enjoy a safer, more joyful everyday life.
            </p>
          </div>

          {/* Contact Information */}
          <div>
            <h2>Contact Information</h2>
            <div>Phone: (941) 249-2344</div>
            <div>Location: North Port, Florida</div>
          </div>

          {/* Information */}
          <div>
            <h2>Information</h2>
            <div><Link to="/privacy-policy">Privacy Policy</Link></div>
            <div><Link to="/terms-and-conditions">Terms &amp; Condition</Link></div>
          </div>
        </div>
        <hr />
        <div className="custom-footer-bottom">
          <div>
            © 2026 Gardens of North Port. All rights reserved. FL Lic. #10843. Located at 4900 S Sumter Blvd, North Port, FL 34287. This website is provided for general informational purposes only. Community services, amenities, residence availability, and care options may change without notice. Images and descriptions are representative and may vary. By submitting your information, you consent to being contacted by our team regarding tours, services, and availability.
          </div>
        </div>
      </footer>

      {/* Gallery Lightbox */}
      <Lightbox
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        index={galleryIndex}
        slides={galleryImages.map(img => ({
          src: `${process.env.PUBLIC_URL}/assets/${img.src}`,
          alt: img.alt,
          title: img.alt
        }))}
      />

      {/* Residence Lightbox */}
      <Lightbox
        open={residenceOpen}
        close={() => setResidenceOpen(false)}
        index={residenceIndex}
        slides={residenceImages.map(img => ({
          src: `${process.env.PUBLIC_URL}/assets/${img.src}`,
          alt: img.alt,
          title: img.title
        }))}
      />
    </div>
    );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
